import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import type { DeviationAnalysisExportCardCopy } from './DeviationAnalysisExportCard'
import type { DeviationAnalysisWidgetCopy } from './DeviationAnalysisWidget'
import { DEVIATION_ANALYSIS_LABELS } from './constants'
import type { DeviationAnalysisExportCopy } from './exportDeviationAnalysis'

export function getDeviationAnalysisExportCardCopy(tExport: TFunction): DeviationAnalysisExportCardCopy {
    return {
        sectionTitle: tExport('sectionTitle'),
        downloadTooltip: tExport('deviationAnalysis.deviationAnalysisDownloadTooltip'),
        downloadButtonLabel: tExport('downloadExcelButtonLabel'),
    }
}

export function getDeviationAnalysisExportCopy(tCommon: TFunction, tExport: TFunction): DeviationAnalysisExportCopy {
    return {
        fileNamePrefix: tExport('deviationAnalysis.deviationAnalysisFileNamePrefix'),
        worksheetName: tExport('deviationAnalysis.deviationAnalysisWorksheetName'),
        assetLabel: tExport('deviationAnalysis.deviationAnalysisAssetLabel'),
        valueLabel: tExport('deviationAnalysis.deviationAnalysisValueColumnLabel'),
        changeLabel: tExport('deviationAnalysis.deviationAnalysisChangeColumnLabel'),
        changePercentageLabel: tExport('deviationAnalysis.deviationAnalysisChangePercentageColumnLabel'),
        remainingUsefulLifePrefix: tExport('deviationAnalysis.deviationAnalysisRemainingUsefulLifePrefix'),
        percentagePointUnit: tCommon('percentagePoints'),
        customRowLabels: {
            'Wert pro m²': tCommon('valuePerSquareMeter'),
            'Wert pro m² WNFl.': tCommon('valuePerSquareMeterRentableArea'),
            'Wert pro m² BGF.': tCommon('valuePerSquareMeterGfa'),
            'Wert pro m² Boden': tCommon('valuePerSquareMeterLand'),
        },
        rowLabels: {
            marketValue: tCommon('marketValue'),
            mortgageLendingValue: tExport('deviationAnalysis.deviationAnalysisRowMortgageLendingValue'),
            landValue: tCommon('valuationMethods.land_value'),
            incomeMarketValue: tCommon('valuationMethods.income'),
            costMarketValue: tCommon('valuationMethods.cost'),
            comparisonMarketValue: tCommon('valuationMethods.comparison'),
            transactionCosts: tExport('deviationAnalysis.deviationAnalysisRowTransactionCosts'),
            grossCapitalValue: tCommon('grossCapitalValue'),
            ancillaryAcquisitionCosts: tCommon('ancillaryAcquisitionCosts'),
            netCapitalValue: tCommon('netCapitalValue'),
            totalSpecialPropertyFeatures: tExport('deviationAnalysis.deviationAnalysisRowTotalSpecialPropertyFeatures'),
            perAreaValue: tCommon('valuePerSquareMeter'),
            grossRentalIncome: tExport('deviationAnalysis.deviationAnalysisRowGrossRentalIncome'),
            contractualRentalIncome: tExport('deviationAnalysis.deviationAnalysisRowContractualRentalIncome'),
            managementCosts: tExport('deviationAnalysis.deviationAnalysisRowManagementCosts'),
            maintenanceCost: tExport('deviationAnalysis.deviationAnalysisRowMaintenanceCost'),
            administrativeCost: tExport('deviationAnalysis.deviationAnalysisRowAdministrativeCost'),
            operatingCost: tExport('deviationAnalysis.deviationAnalysisRowOperatingCost'),
            lossOfRentRiskCost: tExport('deviationAnalysis.deviationAnalysisRowLossOfRentRiskCost'),
            netRentalIncome: tExport('deviationAnalysis.deviationAnalysisRowNetRentalIncome'),
            netIncomeConstruction: tExport('deviationAnalysis.deviationAnalysisRowNetIncomeConstruction'),
            valueConstruction: tExport('deviationAnalysis.deviationAnalysisRowValueConstruction'),
            grossYieldMultiplier: tExport('deviationAnalysis.deviationAnalysisRowGrossYieldMultiplier'),
            netYieldMultiplier: tExport('deviationAnalysis.deviationAnalysisRowNetYieldMultiplier'),
            grossYield: tExport('deviationAnalysis.deviationAnalysisRowGrossYield'),
            netYield: tCommon('netYield'),
            grossInitialYield: tCommon('grossInitialYield'),
            discountRate: tCommon('discountRate'),
            grossFloorArea: tExport('deviationAnalysis.deviationAnalysisRowGrossFloorArea'),
            rentableArea: tCommon('rentableArea'),
        },
    }
}

export function getDeviationAnalysisWidgetCopy(
    exportCopy: DeviationAnalysisExportCopy,
    tDeviationAnalysisWidget: TFunction,
    comparisonValuationDate?: string
): DeviationAnalysisWidgetCopy {
    const comparisonValuationYear = comparisonValuationDate ? dayjs(comparisonValuationDate).format('YYYY') : undefined

    return {
        sectionLabels: {
            marketValueResults: tDeviationAnalysisWidget('sections.marketValueResults'),
            keyEarningsFigures: tDeviationAnalysisWidget('sections.keyEarningsFigures'),
            costs: tDeviationAnalysisWidget('sections.costs'),
            yieldFigures: tDeviationAnalysisWidget('sections.yieldFigures'),
            valuationRelevantAreas: tDeviationAnalysisWidget('sections.valuationRelevantAreas'),
            valuationStructureLifeSpans: tDeviationAnalysisWidget('sections.valuationStructureLifeSpans'),
        },
        rowLabels: {
            ...DEVIATION_ANALYSIS_LABELS,
            ...exportCopy.rowLabels,
        },
        compactComparisonLabel: comparisonValuationYear
            ? tDeviationAnalysisWidget('headers.previousValuationWithYear', { year: comparisonValuationYear })
            : tDeviationAnalysisWidget('headers.previousValuation'),
        currentLabel: tDeviationAnalysisWidget('headers.current'),
        changeLabel: tDeviationAnalysisWidget('headers.change'),
        percentagePointUnit: exportCopy.percentagePointUnit,
        yearUnitLabels: {
            one: tDeviationAnalysisWidget('units.year'),
            other: tDeviationAnalysisWidget('units.years'),
        },
        selectOptionLabels: {
            lockedDraftTitle: tDeviationAnalysisWidget('selectOptions.lockedDraftTitle'),
            lockedValuationTitle: tDeviationAnalysisWidget('selectOptions.lockedValuationTitle'),
            draftStatus: tDeviationAnalysisWidget('selectOptions.draftStatus'),
            finalStatus: tDeviationAnalysisWidget('selectOptions.finalStatus'),
        },
    }
}
